// 📱 태블릿 전용 마이그레이션 스크립트
// [배경] 태블릿 데이터의 ID 불일치 문제 해결
// [실행] 브라우저 콘솔에서:
//   - window.__scanTabletData(): 현재 태블릿 데이터 스캔
//   - window.__diagnoseTabletPrices(): 가격 매칭 상세 진단
//   - window.__previewTabletMigration(): 마이그레이션 미리보기
//   - window.__migrateTablets(): 실제 마이그레이션 실행
//
// ⚠️ 태블릿 ID 문제 유형:
// 1. storage가 ID에 포함: tablet-samsung-...-256gb-wifi-139
// 2. connectivity가 ID에 포함: ...-cellular, ...-wifi
// 3. 순번이 ID에 포함: ...-139, ...-80
// 4. 이 모든 것이 조합되어 있는 경우

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  generateModelId,
  generatePriceId,
  DeviceCatalogDocument,
  UnifiedPriceDocument,
  isCanonicalModelId,
  extractPureModelId
} from '../types/deviceCatalog';

// 🔑 태블릿 ID 정규화 함수
// storage, connectivity, 순번을 제거하고 순수 모델 ID만 추출
const normalizeTabletModelId = (modelId: string): string => {
  let normalized = modelId;
  
  // 1. 끝에 있는 순번 제거 (3자리 이상 숫자)
  normalized = normalized.replace(/-\d{3,}$/, '');
  
  // 2. 끝에 있는 2자리 순번도 제거 (storage/connectivity 뒤에 오는 경우)
  // 예: -256gb-wifi-80 → -256gb-wifi → -256gb
  if (normalized.match(/-(wifi|cellular|lte|5g)-\d+$/i)) {
    normalized = normalized.replace(/-\d+$/, '');
  }
  
  // 3. connectivity 제거 (끝에 있는 경우)
  normalized = normalized.replace(/-(wifi|cellular|lte|5g|wi-fi\+cellular|wificellular)$/i, '');
  
  // 4. storage 제거 (끝에 있는 경우)
  normalized = normalized.replace(/-\d+(gb|tb)$/i, '');
  
  // 5. 중간에 있는 storage-connectivity-순번 패턴 제거
  // 예: galaxy-tab-s9-256gb-wifi-139 → galaxy-tab-s9
  const storageMatch = normalized.match(/-(\d+)(gb|tb)-/i);
  if (storageMatch) {
    const idx = normalized.toLowerCase().indexOf(`-${storageMatch[1]}${storageMatch[2].toLowerCase()}-`);
    if (idx !== -1) {
      normalized = normalized.substring(0, idx);
    }
  }
  
  return normalized;
};

// 🔍 태블릿 ID 상태 분석
const getTabletIdStatus = (id: string, brand: string): {
  status: 'normal' | 'abnormal';
  issues: string[];
  suggestedId: string;
} => {
  const issues: string[] = [];
  let suggestedId = id;
  
  // 문제 패턴 검사
  if (id.match(/-\d+(gb|tb)/i)) {
    issues.push('storage 포함');
  }
  if (id.match(/-(wifi|cellular|lte|5g|wificellular)($|-)/i)) {
    issues.push('connectivity 포함');
  }
  if (id.match(/-\d{2,}$/)) {
    issues.push('순번 포함');
  }
  
  if (issues.length > 0) {
    suggestedId = normalizeTabletModelId(id);
    return { status: 'abnormal', issues, suggestedId };
  }
  
  return { status: 'normal', issues: [], suggestedId: id };
};

/**
 * 🔍 태블릿 데이터 스캔
 */
export async function scanTabletData(): Promise<{
  catalog: { total: number; normal: number; abnormal: number; items: any[] };
  prices: { total: number; normal: number; abnormal: number; orphan: number; items: any[] };
}> {
  console.log('📱 태블릿 데이터 스캔 시작...');
  console.log('============================================');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  // 태블릿 모델 필터링
  console.log('\n📦 device_catalog 태블릿 스캔...');
  const tabletModels: any[] = [];
  const tabletModelIds = new Set<string>();
  
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet') {
      const status = getTabletIdStatus(docSnap.id, data.brand);
      tabletModels.push({
        id: docSnap.id,
        brand: data.brand,
        modelKo: data.modelKo,
        ...status
      });
      tabletModelIds.add(docSnap.id);
    }
  });
  
  // 태블릿 가격 필터링
  console.log('💰 unified_prices 태블릿 스캔...');
  const tabletPrices: any[] = [];
  
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet') {
      const modelId = data.modelId || '';
      const isOrphan = modelId && !tabletModelIds.has(modelId);
      const status = getTabletIdStatus(modelId, data.brand);
      
      tabletPrices.push({
        id: docSnap.id,
        modelId,
        brand: data.brand,
        variant: data.variant,
        gradePrices: data.gradePrices,
        isOrphan,
        ...status
      });
    }
  });
  
  // 통계 계산
  const normalModels = tabletModels.filter(m => m.status === 'normal');
  const abnormalModels = tabletModels.filter(m => m.status === 'abnormal');
  const normalPrices = tabletPrices.filter(p => p.status === 'normal' && !p.isOrphan);
  const abnormalPrices = tabletPrices.filter(p => p.status === 'abnormal');
  const orphanPrices = tabletPrices.filter(p => p.isOrphan);
  
  // 결과 출력
  console.log('\n============================================');
  console.log('📊 태블릿 스캔 결과:');
  console.log(`📦 device_catalog: ${tabletModels.length}개`);
  console.log(`  ✅ 정상 ID: ${normalModels.length}개`);
  console.log(`  ⚠️ 비정상 ID: ${abnormalModels.length}개`);
  
  // 브랜드별 분류
  const byBrand: Record<string, { normal: number; abnormal: number }> = {};
  tabletModels.forEach(m => {
    if (!byBrand[m.brand]) byBrand[m.brand] = { normal: 0, abnormal: 0 };
    if (m.status === 'normal') byBrand[m.brand].normal++;
    else byBrand[m.brand].abnormal++;
  });
  
  console.log('\n  📊 브랜드별:');
  Object.entries(byBrand).forEach(([brand, stats]) => {
    console.log(`    ${brand}: 정상 ${stats.normal}개, 비정상 ${stats.abnormal}개`);
  });
  
  // 샘플 출력 (비정상 모델 최대 10개)
  if (abnormalModels.length > 0) {
    console.log(`\n  📋 비정상 ID 샘플 (${Math.min(10, abnormalModels.length)}/${abnormalModels.length}개):`);
    abnormalModels.slice(0, 10).forEach(m => {
      console.log(`    ⚠️ ${m.brand} - ${m.modelKo}`);
      console.log(`       현재: ${m.id}`);
      console.log(`       제안: ${m.suggestedId}`);
      console.log(`       문제: ${m.issues.join(', ')}`);
    });
  }
  
  console.log(`\n💰 unified_prices: ${tabletPrices.length}개`);
  console.log(`  ✅ 정상 modelId: ${normalPrices.length}개`);
  console.log(`  ⚠️ 비정상 modelId: ${abnormalPrices.length}개`);
  console.log(`  🔗 고아 가격: ${orphanPrices.length}개`);
  
  // 고아 가격 샘플
  if (orphanPrices.length > 0) {
    console.log(`\n  📋 고아 가격 샘플 (${Math.min(5, orphanPrices.length)}/${orphanPrices.length}개):`);
    orphanPrices.slice(0, 5).forEach(p => {
      console.log(`    🔗 ${p.id}`);
      console.log(`       modelId: ${p.modelId} (device_catalog에 없음)`);
    });
  }
  
  console.log('\n============================================');
  
  return {
    catalog: {
      total: tabletModels.length,
      normal: normalModels.length,
      abnormal: abnormalModels.length,
      items: tabletModels
    },
    prices: {
      total: tabletPrices.length,
      normal: normalPrices.length,
      abnormal: abnormalPrices.length,
      orphan: orphanPrices.length,
      items: tabletPrices
    }
  };
}

/**
 * 🔍 태블릿 가격 매칭 상세 진단
 */
export async function diagnoseTabletPrices(): Promise<void> {
  console.log('🔍 태블릿 가격 매칭 진단 시작...');
  console.log('============================================');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  // 태블릿 모델 수집
  const tabletModels: DeviceCatalogDocument[] = [];
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet') {
      tabletModels.push({ id: docSnap.id, ...data } as DeviceCatalogDocument);
    }
  });
  
  // 태블릿 가격 수집
  const tabletPrices: UnifiedPriceDocument[] = [];
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet') {
      tabletPrices.push({ id: docSnap.id, ...data } as UnifiedPriceDocument);
    }
  });
  
  console.log(`📦 태블릿 모델: ${tabletModels.length}개`);
  console.log(`💰 태블릿 가격: ${tabletPrices.length}개`);
  
  // 브랜드별로 분류
  const appleModels = tabletModels.filter(m => m.brand === 'Apple');
  const samsungModels = tabletModels.filter(m => m.brand === 'Samsung');
  
  console.log(`\n  Apple: ${appleModels.length}개`);
  console.log(`  Samsung: ${samsungModels.length}개`);
  
  // 매칭 테스트 (Apple 샘플)
  console.log('\n🔗 Apple 태블릿 매칭 테스트 (처음 5개):');
  appleModels.slice(0, 5).forEach(model => {
    const normalizedModelId = normalizeTabletModelId(model.id);
    
    // 정확한 modelId 매칭
    const exactMatch = tabletPrices.filter(p => p.modelId === model.id);
    
    // 정규화된 modelId 매칭
    const normalizedMatch = tabletPrices.filter(p => 
      normalizeTabletModelId(p.modelId || '') === normalizedModelId
    );
    
    console.log(`\n  📱 ${model.modelKo}`);
    console.log(`     id: ${model.id}`);
    console.log(`     normalized: ${normalizedModelId}`);
    console.log(`     정확한 매칭: ${exactMatch.length}개`);
    console.log(`     정규화 매칭: ${normalizedMatch.length}개`);
    
    if (normalizedMatch.length > 0) {
      normalizedMatch.slice(0, 3).forEach(p => {
        console.log(`       - ${p.id} (A: ${p.gradePrices?.A || 0}원)`);
      });
    }
  });
  
  // Samsung 샘플
  console.log('\n🔗 Samsung 태블릿 매칭 테스트 (처음 5개):');
  samsungModels.slice(0, 5).forEach(model => {
    const normalizedModelId = normalizeTabletModelId(model.id);
    
    const exactMatch = tabletPrices.filter(p => p.modelId === model.id);
    const normalizedMatch = tabletPrices.filter(p => 
      normalizeTabletModelId(p.modelId || '') === normalizedModelId
    );
    
    console.log(`\n  📱 ${model.modelKo}`);
    console.log(`     id: ${model.id}`);
    console.log(`     normalized: ${normalizedModelId}`);
    console.log(`     정확한 매칭: ${exactMatch.length}개`);
    console.log(`     정규화 매칭: ${normalizedMatch.length}개`);
    
    if (normalizedMatch.length > 0) {
      normalizedMatch.slice(0, 3).forEach(p => {
        console.log(`       - ${p.id} (A: ${p.gradePrices?.A || 0}원)`);
      });
    }
  });
  
  console.log('\n============================================');
}

/**
 * 🔍 태블릿 마이그레이션 미리보기
 */
export async function previewTabletMigration(): Promise<void> {
  console.log('📱 태블릿 마이그레이션 미리보기 (Dry Run)');
  console.log('============================================');
  console.log('⚠️ 이 작업은 데이터를 변경하지 않습니다.\n');
  
  const scanResult = await scanTabletData();
  
  // 변환 계획 수립
  const catalogChanges: { oldId: string; newId: string; modelKo: string }[] = [];
  const priceChanges: { priceId: string; oldModelId: string; newModelId: string }[] = [];
  
  // 1. device_catalog 변환 계획
  scanResult.catalog.items.forEach(model => {
    if (model.status === 'abnormal' && model.id !== model.suggestedId) {
      catalogChanges.push({
        oldId: model.id,
        newId: model.suggestedId,
        modelKo: model.modelKo
      });
    }
  });
  
  // 2. unified_prices modelId 변환 계획
  scanResult.prices.items.forEach(price => {
    if (price.status === 'abnormal' && price.modelId !== price.suggestedId) {
      priceChanges.push({
        priceId: price.id,
        oldModelId: price.modelId,
        newModelId: price.suggestedId
      });
    }
  });
  
  console.log('\n🔄 마이그레이션 예정 작업:');
  console.log(`\n📦 device_catalog ${catalogChanges.length}개 변환 예정:`);
  
  // 샘플 출력 (최대 10개)
  catalogChanges.slice(0, 10).forEach(change => {
    console.log(`  ${change.modelKo}`);
    console.log(`    ${change.oldId}`);
    console.log(`    → ${change.newId}`);
  });
  
  if (catalogChanges.length > 10) {
    console.log(`  ... 그 외 ${catalogChanges.length - 10}개 더 있음`);
  }
  
  console.log(`\n💰 unified_prices ${priceChanges.length}개 modelId 수정 예정:`);
  
  priceChanges.slice(0, 10).forEach(change => {
    console.log(`  ${change.priceId}`);
    console.log(`    modelId: ${change.oldModelId}`);
    console.log(`    → ${change.newModelId}`);
  });
  
  if (priceChanges.length > 10) {
    console.log(`  ... 그 외 ${priceChanges.length - 10}개 더 있음`);
  }
  
  // 중복 ID 경고
  const newIdCounts = new Map<string, number>();
  catalogChanges.forEach(c => {
    newIdCounts.set(c.newId, (newIdCounts.get(c.newId) || 0) + 1);
  });
  
  const duplicates = Array.from(newIdCounts.entries()).filter(([_, count]) => count > 1);
  if (duplicates.length > 0) {
    console.log(`\n⚠️ 주의: ${duplicates.length}개 ID가 중복됩니다!`);
    console.log('   이 모델들은 병합이 필요합니다:');
    duplicates.slice(0, 5).forEach(([id, count]) => {
      console.log(`   - ${id}: ${count}개 모델이 같은 ID로 변환됨`);
      const models = catalogChanges.filter(c => c.newId === id);
      models.forEach(m => console.log(`     · ${m.modelKo} (${m.oldId})`));
    });
  }
  
  console.log('\n============================================');
  console.log('📌 실제 마이그레이션을 실행하려면:');
  console.log('   await window.__migrateTablets()');
}

/**
 * 🔄 태블릿 마이그레이션 실행
 * ⚠️ 주의: 이 작업은 데이터를 변경합니다!
 */
export async function migrateTablets(): Promise<void> {
  console.log('📱 태블릿 마이그레이션 시작...');
  console.log('============================================');
  console.log('⚠️ 이 작업은 데이터를 변경합니다!\n');
  
  const scanResult = await scanTabletData();
  
  let catalogUpdated = 0;
  let catalogMerged = 0;
  let pricesUpdated = 0;
  const errors: string[] = [];
  
  // 1. 중복 ID 그룹 파악
  const idGroups = new Map<string, string[]>(); // newId → [oldId1, oldId2, ...]
  scanResult.catalog.items.forEach(model => {
    if (model.status === 'abnormal') {
      const newId = model.suggestedId;
      if (!idGroups.has(newId)) {
        idGroups.set(newId, []);
      }
      idGroups.get(newId)!.push(model.id);
    }
  });
  
  console.log('🔧 1단계: device_catalog 정리...');
  
  for (const [newId, oldIds] of idGroups) {
    try {
      // 이미 canonical ID가 존재하는지 확인
      const existingDoc = await getDoc(doc(db, 'device_catalog', newId));
      
      if (existingDoc.exists()) {
        // 이미 존재하면 old ID들만 삭제
        for (const oldId of oldIds) {
          if (oldId !== newId) {
            await deleteDoc(doc(db, 'device_catalog', oldId));
            console.log(`  🗑️ 삭제 (중복): ${oldId}`);
            catalogMerged++;
          }
        }
      } else {
        // 첫 번째 모델을 canonical ID로 복사
        const firstOldId = oldIds[0];
        const firstDocSnap = await getDoc(doc(db, 'device_catalog', firstOldId));
        
        if (firstDocSnap.exists()) {
          const data = firstDocSnap.data();
          
          // 모든 storageOptions 병합
          const allStorageOptions = new Set<string>();
          const allConnectivityOptions = new Set<string>();
          
          for (const oldId of oldIds) {
            const oldDocSnap = await getDoc(doc(db, 'device_catalog', oldId));
            if (oldDocSnap.exists()) {
              const oldData = oldDocSnap.data();
              (oldData.storageOptions || []).forEach((s: string) => allStorageOptions.add(s));
              (oldData.connectivityOptions || []).forEach((c: string) => allConnectivityOptions.add(c));
            }
          }
          
          // 새 문서 생성
          await setDoc(doc(db, 'device_catalog', newId), {
            ...data,
            id: newId,
            storageOptions: Array.from(allStorageOptions),
            connectivityOptions: Array.from(allConnectivityOptions),
            updatedAt: Timestamp.now(),
            updatedBy: 'tablet-migration'
          });
          
          console.log(`  ✅ 생성: ${newId}`);
          console.log(`     storage: ${Array.from(allStorageOptions).join(', ')}`);
          catalogUpdated++;
          
          // 기존 문서들 삭제
          for (const oldId of oldIds) {
            if (oldId !== newId) {
              await deleteDoc(doc(db, 'device_catalog', oldId));
              console.log(`  🗑️ 삭제: ${oldId}`);
              catalogMerged++;
            }
          }
        }
      }
    } catch (error) {
      errors.push(`catalog ${newId}: ${(error as Error).message}`);
    }
  }
  
  // 2. unified_prices의 modelId 업데이트
  console.log('\n🔧 2단계: unified_prices modelId 업데이트...');
  
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  for (const docSnap of pricesSnapshot.docs) {
    const data = docSnap.data();
    if (data.deviceType !== 'tablet') continue;
    
    const modelId = data.modelId || '';
    const status = getTabletIdStatus(modelId, data.brand);
    
    if (status.status === 'abnormal' && modelId !== status.suggestedId) {
      try {
        await setDoc(doc(db, 'unified_prices', docSnap.id), {
          ...data,
          modelId: status.suggestedId,
          updatedAt: Timestamp.now(),
          updatedBy: 'tablet-migration'
        });
        
        console.log(`  ✅ ${docSnap.id}`);
        console.log(`     modelId: ${modelId} → ${status.suggestedId}`);
        pricesUpdated++;
      } catch (error) {
        errors.push(`price ${docSnap.id}: ${(error as Error).message}`);
      }
    }
  }
  
  // 결과 출력
  console.log('\n============================================');
  console.log('📊 마이그레이션 결과:');
  console.log(`  ✅ device_catalog 생성: ${catalogUpdated}개`);
  console.log(`  🗑️ device_catalog 병합/삭제: ${catalogMerged}개`);
  console.log(`  ✅ unified_prices modelId 수정: ${pricesUpdated}개`);
  
  if (errors.length > 0) {
    console.log(`\n❌ 에러: ${errors.length}개`);
    errors.slice(0, 10).forEach(e => console.log(`  - ${e}`));
  } else {
    console.log('\n🎉 태블릿 마이그레이션 완료!');
    console.log('\n📌 다음 단계:');
    console.log('   await window.__scanTabletData()  // 결과 확인');
  }
}

/**
 * 🔍 특정 브랜드 태블릿만 스캔
 */
export async function scanTabletsByBrand(brand: 'Apple' | 'Samsung'): Promise<void> {
  console.log(`📱 ${brand} 태블릿 스캔...`);
  console.log('============================================');
  
  const scanResult = await scanTabletData();
  
  const brandModels = scanResult.catalog.items.filter(m => m.brand === brand);
  const brandPrices = scanResult.prices.items.filter(p => p.brand === brand);
  
  console.log(`\n📦 ${brand} 모델: ${brandModels.length}개`);
  console.log(`  ✅ 정상: ${brandModels.filter(m => m.status === 'normal').length}개`);
  console.log(`  ⚠️ 비정상: ${brandModels.filter(m => m.status === 'abnormal').length}개`);
  
  console.log(`\n💰 ${brand} 가격: ${brandPrices.length}개`);
  console.log(`  ✅ 정상: ${brandPrices.filter(p => p.status === 'normal' && !p.isOrphan).length}개`);
  console.log(`  ⚠️ 비정상: ${brandPrices.filter(p => p.status === 'abnormal').length}개`);
  console.log(`  🔗 고아: ${brandPrices.filter(p => p.isOrphan).length}개`);
  
  // 상세 목록
  console.log('\n📋 비정상 모델 목록:');
  brandModels.filter(m => m.status === 'abnormal').forEach(m => {
    console.log(`  ${m.modelKo}`);
    console.log(`    현재: ${m.id}`);
    console.log(`    제안: ${m.suggestedId}`);
    console.log(`    문제: ${m.issues.join(', ')}`);
  });
}

/**
 * 🔍 중복 병합 상세 분석 (11인치/13인치 등 사이즈 구분 확인)
 */
export async function analyzeMergeConflicts(): Promise<void> {
  console.log('🔍 중복 병합 상세 분석');
  console.log('============================================');
  console.log('⚠️ 같은 ID로 병합되는 모델들 중 실제로 다른 제품인지 확인합니다.\n');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  
  // 태블릿 모델 수집 및 정규화
  const tabletModels: any[] = [];
  
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet') {
      const normalizedId = normalizeTabletModelId(docSnap.id);
      tabletModels.push({
        id: docSnap.id,
        normalizedId,
        brand: data.brand,
        modelKo: data.modelKo,
        modelEn: data.modelEn,
        series: data.series,
        storageOptions: data.storageOptions,
        connectivityOptions: data.connectivityOptions
      });
    }
  });
  
  // 정규화된 ID별 그룹화
  const groups = new Map<string, any[]>();
  tabletModels.forEach(model => {
    if (!groups.has(model.normalizedId)) {
      groups.set(model.normalizedId, []);
    }
    groups.get(model.normalizedId)!.push(model);
  });
  
  // 2개 이상 모델이 같은 ID로 병합되는 경우만 필터
  const mergeGroups = Array.from(groups.entries())
    .filter(([_, models]) => models.length > 1);
  
  console.log(`📊 총 ${mergeGroups.length}개 그룹이 병합 예정\n`);
  
  // 각 그룹 분석 - 모델명이 다른 경우 경고
  const conflicts: { normalizedId: string; models: any[] }[] = [];
  const normalMerges: { normalizedId: string; count: number }[] = [];
  
  mergeGroups.forEach(([normalizedId, models]) => {
    // 모델명(modelKo)이 서로 다른지 확인
    const uniqueNames = new Set(models.map(m => m.modelKo));
    
    if (uniqueNames.size > 1) {
      // 다른 모델명이 있음 - 잠재적 충돌
      conflicts.push({ normalizedId, models });
    } else {
      // 같은 모델명 - 정상 병합
      normalMerges.push({ normalizedId, count: models.length });
    }
  });
  
  // 정상 병합 출력 (간략히)
  console.log(`✅ 정상 병합 (같은 모델명): ${normalMerges.length}개 그룹`);
  normalMerges.slice(0, 5).forEach(m => {
    console.log(`   - ${m.normalizedId}: ${m.count}개 variant`);
  });
  if (normalMerges.length > 5) {
    console.log(`   ... 그 외 ${normalMerges.length - 5}개 더 있음`);
  }
  
  // 충돌 케이스 상세 출력
  if (conflicts.length > 0) {
    console.log(`\n⚠️ 주의 필요 (다른 모델명): ${conflicts.length}개 그룹`);
    console.log('   → 이 그룹들은 실제로 다른 제품일 수 있습니다!\n');
    
    conflicts.forEach(({ normalizedId, models }) => {
      console.log(`\n🔴 ${normalizedId}`);
      console.log('   병합될 모델들:');
      models.forEach(m => {
        console.log(`   - "${m.modelKo}"`);
        console.log(`     id: ${m.id}`);
        console.log(`     series: ${m.series}`);
        console.log(`     storage: ${(m.storageOptions || []).join(', ')}`);
      });
      
      // 모델명 차이 분석
      const names = models.map(m => m.modelKo);
      const uniqueNames = [...new Set(names)];
      console.log(`   📋 고유 모델명: ${uniqueNames.join(' / ')}`);
      
      // 인치 정보 추출
      const inches = names.map(n => {
        const match = n.match(/(\d+\.?\d*)\s*인치/);
        return match ? match[1] + '인치' : null;
      }).filter(Boolean);
      
      if (inches.length > 0) {
        const uniqueInches = [...new Set(inches)];
        if (uniqueInches.length > 1) {
          console.log(`   ⚠️ 다른 사이즈 발견: ${uniqueInches.join(', ')}`);
        }
      }
    });
    
    console.log('\n============================================');
    console.log('💡 권장 조치:');
    console.log('   1. 위 충돌 케이스를 확인하세요.');
    console.log('   2. 실제로 다른 제품이라면 ID 정규화 규칙을 수정해야 합니다.');
    console.log('   3. 같은 제품의 variant라면 병합해도 됩니다.');
  } else {
    console.log('\n✅ 모든 병합 그룹이 같은 모델명을 가지고 있습니다.');
    console.log('   안전하게 마이그레이션을 진행할 수 있습니다.');
  }
  
  console.log('\n============================================');
}

/**
 * 🔍 Samsung 태블릿 가격 매칭 상세 진단
 */
export async function diagnoseSamsungTabletPrices(): Promise<void> {
  console.log('🔍 Samsung 태블릿 가격 매칭 상세 진단');
  console.log('============================================');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  // Samsung 태블릿 모델 수집
  const samsungModels: any[] = [];
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet' && data.brand === 'Samsung') {
      samsungModels.push({
        id: docSnap.id,
        modelKo: data.modelKo,
        storageOptions: data.storageOptions || [],
        connectivityOptions: data.connectivityOptions || []
      });
    }
  });
  
  // Samsung 태블릿 가격 수집
  const samsungPrices: any[] = [];
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet' && data.brand === 'Samsung') {
      samsungPrices.push({
        id: docSnap.id,
        modelId: data.modelId,
        variant: data.variant,
        gradePrices: data.gradePrices
      });
    }
  });
  
  console.log(`📦 Samsung 태블릿 모델: ${samsungModels.length}개`);
  console.log(`💰 Samsung 태블릿 가격: ${samsungPrices.length}개`);
  
  // 각 모델별 가격 매칭 확인
  console.log('\n🔗 모델-가격 매칭 확인:');
  
  let matchedModels = 0;
  let unmatchedModels = 0;
  
  samsungModels.forEach(model => {
    // 정확한 modelId 매칭
    const matchedPrices = samsungPrices.filter(p => p.modelId === model.id);
    
    if (matchedPrices.length > 0) {
      matchedModels++;
      console.log(`\n  ✅ ${model.modelKo}`);
      console.log(`     id: ${model.id}`);
      console.log(`     storage: ${model.storageOptions.join(', ')}`);
      console.log(`     매칭된 가격: ${matchedPrices.length}개`);
      matchedPrices.slice(0, 3).forEach(p => {
        const hasPrice = p.gradePrices && (p.gradePrices.A > 0 || p.gradePrices.B > 0);
        console.log(`       - ${p.id} (A: ${p.gradePrices?.A || 0}원) ${hasPrice ? '✅' : '❌ 가격없음'}`);
      });
      if (matchedPrices.length > 3) {
        console.log(`       ... 그 외 ${matchedPrices.length - 3}개 더 있음`);
      }
    } else {
      unmatchedModels++;
      console.log(`\n  ❌ ${model.modelKo}`);
      console.log(`     id: ${model.id}`);
      console.log(`     storage: ${model.storageOptions.join(', ')}`);
      console.log(`     매칭된 가격: 0개`);
      
      // 유사한 가격 찾기
      const similarPrices = samsungPrices.filter(p => 
        p.modelId?.includes(model.id.split('-').slice(-2).join('-')) ||
        p.id?.includes(model.id.split('-').slice(-2).join('-'))
      );
      
      if (similarPrices.length > 0) {
        console.log(`     💡 유사한 가격 ${similarPrices.length}개 발견:`);
        similarPrices.slice(0, 2).forEach(p => {
          console.log(`       - modelId: ${p.modelId}`);
        });
      }
    }
  });
  
  // 고아 가격 (모델에 매칭 안 되는 가격)
  const modelIds = new Set(samsungModels.map(m => m.id));
  const orphanPrices = samsungPrices.filter(p => !modelIds.has(p.modelId));
  
  console.log('\n============================================');
  console.log('📊 Samsung 태블릿 매칭 요약:');
  console.log(`  ✅ 가격 있는 모델: ${matchedModels}개`);
  console.log(`  ❌ 가격 없는 모델: ${unmatchedModels}개`);
  console.log(`  🔗 고아 가격: ${orphanPrices.length}개`);
  
  if (orphanPrices.length > 0) {
    console.log('\n🔗 고아 가격 목록 (device_catalog에 모델 없음):');
    orphanPrices.slice(0, 10).forEach(p => {
      console.log(`  - ${p.id}`);
      console.log(`    modelId: ${p.modelId}`);
    });
    if (orphanPrices.length > 10) {
      console.log(`  ... 그 외 ${orphanPrices.length - 10}개 더 있음`);
    }
  }
  
  console.log('\n============================================');
}

/**
 * 🔍 특정 모델 상세 조회
 */
export async function inspectModel(keyword: string): Promise<void> {
  console.log(`🔍 "${keyword}" 포함 모델 검색...`);
  console.log('============================================');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  const keywordLower = keyword.toLowerCase();
  
  // 모델 검색
  const matchingModels: any[] = [];
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet') {
      const searchStr = `${docSnap.id} ${data.modelKo || ''} ${data.modelEn || ''}`.toLowerCase();
      if (searchStr.includes(keywordLower)) {
        matchingModels.push({
          id: docSnap.id,
          normalizedId: normalizeTabletModelId(docSnap.id),
          ...data
        });
      }
    }
  });
  
  // 가격 검색
  const matchingPrices: any[] = [];
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'tablet') {
      const searchStr = `${docSnap.id} ${data.modelId || ''}`.toLowerCase();
      if (searchStr.includes(keywordLower)) {
        matchingPrices.push({
          id: docSnap.id,
          ...data
        });
      }
    }
  });
  
  console.log(`\n📦 device_catalog: ${matchingModels.length}개 발견`);
  matchingModels.forEach(m => {
    console.log(`\n  📱 ${m.modelKo}`);
    console.log(`     id: ${m.id}`);
    console.log(`     normalizedId: ${m.normalizedId}`);
    console.log(`     brand: ${m.brand}`);
    console.log(`     series: ${m.series}`);
    console.log(`     modelEn: ${m.modelEn}`);
    console.log(`     storage: ${(m.storageOptions || []).join(', ')}`);
    console.log(`     connectivity: ${(m.connectivityOptions || []).join(', ')}`);
  });
  
  console.log(`\n💰 unified_prices: ${matchingPrices.length}개 발견`);
  matchingPrices.slice(0, 10).forEach(p => {
    console.log(`\n  💵 ${p.id}`);
    console.log(`     modelId: ${p.modelId}`);
    console.log(`     variant: ${JSON.stringify(p.variant)}`);
    console.log(`     A등급: ${p.gradePrices?.A || 0}원`);
  });
  
  if (matchingPrices.length > 10) {
    console.log(`\n  ... 그 외 ${matchingPrices.length - 10}개 더 있음`);
  }
  
  console.log('\n============================================');
}

// 🔌 window 객체에 함수 등록
if (typeof window !== 'undefined') {
  (window as any).__scanTabletData = scanTabletData;
  (window as any).__diagnoseTabletPrices = diagnoseTabletPrices;
  (window as any).__previewTabletMigration = previewTabletMigration;
  (window as any).__migrateTablets = migrateTablets;
  (window as any).__scanTabletsByBrand = scanTabletsByBrand;
  (window as any).__analyzeMergeConflicts = analyzeMergeConflicts;
  (window as any).__inspectModel = inspectModel;
  (window as any).__diagnoseSamsungTabletPrices = diagnoseSamsungTabletPrices;
  
  console.log('📱 태블릿 마이그레이션 함수 등록 완료:');
  console.log('  🔍 진단:');
  console.log('    - window.__scanTabletData(): 태블릿 데이터 스캔');
  console.log('    - window.__diagnoseTabletPrices(): 가격 매칭 진단');
  console.log('    - window.__diagnoseSamsungTabletPrices(): Samsung 태블릿 상세 진단');
  console.log('    - window.__scanTabletsByBrand("Apple" | "Samsung"): 브랜드별 스캔');
  console.log('    - window.__analyzeMergeConflicts(): 중복 병합 충돌 분석');
  console.log('    - window.__inspectModel("키워드"): 특정 모델 상세 조회');
  console.log('  🔄 마이그레이션:');
  console.log('    - window.__previewTabletMigration(): 미리보기');
  console.log('    - window.__migrateTablets(): 실제 실행');
}

